import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Save, RotateCcw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

interface BotPredictionSettingsProps {
  botId: string;
  currentWinThreshold: number;
  currentMaxSequenceLength: number;
  currentMinPatternLength: number;
  currentMaxPatternLength: number;
  currentMinPatternFrequency: number;
  onSettingsUpdate: (
    winThreshold: number, 
    maxSequenceLength: number,
    minPatternLength: number,
    maxPatternLength: number,
    minPatternFrequency: number
  ) => void;
}

export const BotPredictionSettings: React.FC<BotPredictionSettingsProps> = ({
  botId,
  currentWinThreshold,
  currentMaxSequenceLength,
  currentMinPatternLength,
  currentMaxPatternLength,
  currentMinPatternFrequency,
  onSettingsUpdate
}) => {
  const [winThreshold, setWinThreshold] = useState(currentWinThreshold * 100); // Convert to percentage
  const [maxSequenceLength, setMaxSequenceLength] = useState(currentMaxSequenceLength);
  const [minPatternLength, setMinPatternLength] = useState(currentMinPatternLength);
  const [maxPatternLength, setMaxPatternLength] = useState(currentMaxPatternLength);
  const [minPatternFrequency, setMinPatternFrequency] = useState(currentMinPatternFrequency);
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const thresholdDecimal = winThreshold / 100; // Convert back to decimal
      
      const { error } = await supabase
        .from('bots')
        .update({ 
          win_threshold: thresholdDecimal,
          max_sequence_length: maxSequenceLength,
          min_pattern_length: minPatternLength,
          max_pattern_length: maxPatternLength,
          min_pattern_frequency: minPatternFrequency
        })
        .eq('id', botId);

      if (error) throw error;

      onSettingsUpdate(thresholdDecimal, maxSequenceLength, minPatternLength, maxPatternLength, minPatternFrequency);
      toast.success('Bot prediction settings updated successfully!');
    } catch (error) {
      console.error('Error updating bot settings:', error);
      toast.error('Failed to update bot settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setWinThreshold(70); // 70% default
    setMaxSequenceLength(12); // 12 default
    setMinPatternLength(3); // 3 default
    setMaxPatternLength(7); // 7 default
    setMinPatternFrequency(2); // 2 default
  };

  const isValid = winThreshold >= 50 && winThreshold <= 90 && 
                  maxSequenceLength >= 3 && maxSequenceLength <= 20 &&
                  minPatternLength >= 1 && minPatternLength <= 20 &&
                  maxPatternLength >= 1 && maxPatternLength <= 20 &&
                  minPatternLength <= maxPatternLength &&
                  minPatternFrequency >= 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          Prediction Settings
        </CardTitle>
        <CardDescription>
          Configure prediction parameters for this bot
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="winThreshold">Win Threshold (%)</Label>
            <Input
              id="winThreshold"
              type="number"
              min={50}
              max={90}
              value={winThreshold}
              onChange={(e) => setWinThreshold(Number(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Minimum win probability to make a prediction (50-90%)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxSequenceLength">Max Sequence Length</Label>
            <Input
              id="maxSequenceLength"
              type="number"
              min={3}
              max={20}
              value={maxSequenceLength}
              onChange={(e) => setMaxSequenceLength(Number(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Maximum number of trades to analyze for patterns (3-20)
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="minPatternLength">Min Pattern Length</Label>
            <Input
              id="minPatternLength"
              type="number"
              min={1}
              max={20}
              value={minPatternLength}
              onChange={(e) => setMinPatternLength(Number(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Minimum pattern length for analysis (1-20)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxPatternLength">Max Pattern Length</Label>
            <Input
              id="maxPatternLength"
              type="number"
              min={1}
              max={20}
              value={maxPatternLength}
              onChange={(e) => setMaxPatternLength(Number(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Maximum pattern length for analysis (1-20)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="minPatternFrequency">Min Pattern Frequency</Label>
            <Input
              id="minPatternFrequency"
              type="number"
              min={1}
              value={minPatternFrequency}
              onChange={(e) => setMinPatternFrequency(Number(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Minimum pattern frequency threshold (≥1)
            </p>
          </div>
        </div>

        {!isValid && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Please ensure: Win Threshold (50-90%), Max Sequence Length (3-20), Pattern Lengths (1-20), Min ≤ Max Pattern Length, and Min Pattern Frequency (≥1)
            </AlertDescription>
          </Alert>
        )}

        <div className="flex gap-2">
          <Button 
            onClick={handleSave} 
            disabled={!isValid || isLoading}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            Save Settings
          </Button>
          
          <Button 
            variant="outline" 
            onClick={handleReset}
            className="flex items-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Defaults
          </Button>
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <p><strong>Win Threshold:</strong> Higher values = more conservative predictions</p>
          <p><strong>Max Sequence:</strong> Higher values = more context but slower processing</p>
          <p><strong>Pattern Length:</strong> Shorter = fast analysis, longer = more complex patterns</p>
          <p><strong>Pattern Frequency:</strong> Higher values = more reliable but fewer patterns</p>
        </div>
      </CardContent>
    </Card>
  );
};